import { useEffect, useState } from "react";
import { Anchor, Badge, Button, Card, Container, Group, Select, Stack, Text, TextInput, Title } from "@mantine/core";
import { IconCheck, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { api } from "../api/client";
import type { Notification, NotificationType } from "../api/types";
import { NOTIFICATION_TYPES } from "../api/types";

export function NotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<Notification[]>([]);
  const [typeFilter, setTypeFilter] = useState<NotificationType | null>(null);
  const [search, setSearch] = useState("");

  async function load() {
    const res = await api.listNotifications({ limit: 100 });
    setItems(res.notifications);
  }

  useEffect(() => { void load(); }, []);

  const visible = items.filter((n) =>
    (!typeFilter || n.type === typeFilter) &&
    (!search || (n.title + n.body).toLowerCase().includes(search.toLowerCase())),
  );

  async function openItem(n: Notification) {
    if (!n.read_at) await api.markNotificationRead(n.id);
    if (n.link) navigate(n.link);
    else void load();
  }

  return (
    <Container size="md" py="md">
      <Group justify="space-between" mb="sm">
        <Title order={3}>{t("notifications.title")}</Title>
        <Button
          variant="light"
          leftSection={<IconCheck size={14} />}
          onClick={async () => { await api.markAllNotificationsRead(); await load(); }}
        >
          {t("notifications.markAllRead")}
        </Button>
      </Group>
      <Group gap="sm" mb="sm" wrap="wrap">
        <Select
          placeholder={t("notifications.filterType")}
          data={NOTIFICATION_TYPES.map((tp) => ({ value: tp, label: tp }))}
          value={typeFilter}
          onChange={(v) => setTypeFilter(v as NotificationType | null)}
          searchable
          clearable
          w={280}
        />
        <TextInput
          placeholder={t("notifications.search")}
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: "1 1 200px" }}
        />
      </Group>
      <Stack gap="xs">
        {visible.length === 0 && <Text c="dimmed">{t("notifications.empty")}</Text>}
        {visible.map((n) => (
          <Card
            key={n.id}
            withBorder
            style={{ cursor: "pointer", background: n.read_at ? undefined : "var(--mantine-color-blue-light)" }}
            onClick={() => void openItem(n)}
          >
            <Group justify="space-between" mb={4}>
              <Group gap="xs">
                <Badge variant="light">{n.type}</Badge>
                <Text fw={n.read_at ? 400 : 600}>{n.title}</Text>
              </Group>
              <Text size="xs" c="dimmed">{new Date(n.created_at).toLocaleString()}</Text>
            </Group>
            <Text size="sm" c="dimmed">{n.body}</Text>
            {n.link && (
              <Anchor size="xs" mt={4}>{n.link}</Anchor>
            )}
          </Card>
        ))}
      </Stack>
    </Container>
  );
}
